import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  useSeoTags,
  buildWebPageJsonLd,
  buildFaqJsonLd,
} from '../hooks/useSeoTags';
import './FaqPage.css';

type FaqItem = { q: string; a: string };
type FaqSection = { heading: string; items: FaqItem[] };

const FaqPage: React.FC = () => {
  const { t } = useTranslation('faq');
  const sections = t('sections', { returnObjects: true }) as FaqSection[];

  // Flatten all sections into a single FAQ JSON-LD list so Google can show
  // rich-result Q&A accordions on the FAQ SERP entry. Defensive: if the i18n
  // sections array is somehow not yet hydrated (returnObjects can yield the
  // raw key in dev), we fall back to an empty array.
  const flatFaqItems = Array.isArray(sections)
    ? sections.flatMap((s) => s.items || [])
    : [];
  const faqTitle = t('kicker', { defaultValue: 'Frequently Asked Questions' });
  const faqDescription = t('lede');
  const seoTags = useSeoTags({
    title: faqTitle,
    description: faqDescription,
    canonical: '/faq',
    jsonLd: [
      ...buildWebPageJsonLd({
        url: '/faq',
        name: faqTitle,
        description: faqDescription,
        breadcrumbs: [
          { name: 'Loopy', url: '/' },
          { name: faqTitle, url: '/faq' },
        ],
      }),
      buildFaqJsonLd(flatFaqItems),
    ],
  });

  return (
    <article className="faq-page">
      {seoTags}
      <div className="faq-wrap">
        <header className="faq-hero">
          <h1 className="faq-hero__title">{t('lede')}</h1>
        </header>

        {sections.map((section, sIdx) => (
          <section
            key={sIdx}
            className="faq-card"
            aria-labelledby={`faq-section-${sIdx}`}
          >
            <h2 id={`faq-section-${sIdx}`} className="faq-card__heading">
              {section.heading}
            </h2>
            <dl className="faq-list">
              {section.items.map((item, iIdx) => (
                <div key={iIdx} className="faq-item">
                  <dt className="faq-item__q">{item.q}</dt>
                  <dd className="faq-item__a">{item.a}</dd>
                </div>
              ))}
            </dl>
          </section>
        ))}

        <section className="faq-contact">
          <p className="faq-contact__body">
            {t('contact.bodyBefore')}
            <a href={`mailto:${t('contact.email')}`} className="faq-contact__email">
              {t('contact.email')}
            </a>
            {t('contact.bodyAfter')}
          </p>
          <Link to="/contact" className="faq-contact__form-link">
            {t('contact.formLink')}
          </Link>
        </section>

        <footer className="faq-credit">{t('credit')}</footer>
      </div>
    </article>
  );
};

export default FaqPage;
